import { Alert, Box, Flex, Group, SegmentedControl, Tabs } from "@mantine/core";
import { IconChecklist, IconTable } from "@tabler/icons-react";
import { type ReactNode, useMemo } from "react";
import { Userpilot } from "userpilot";
import { AlertPresets } from "../../../mantine/mantinePresets";
import type { EnergyRevenuesCellCheckStateColumn } from "../../../types/api.types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { sortBackendDates } from "../../../utils/dates/sortBackendDates";
import { roundWithPrecision } from "../../../utils/roundWithPrecision";
import type { UserFacingErrorWithButton } from "../../LoadOrError/LoadOrError";
import { ErrorButton } from "../../LoadOrError/LoadOrError";
import { TruncatedList } from "../../TruncatedList/TruncatedList";
import { useEnergyRevenuesPageParams } from "../hooks/useEnergyRevenuesPageParams";
import { useLsEnergyPriceUnit } from "../hooks/useLsEnergyUnit";
import type {
  EnergyPriceUnits,
  EnergyRevenuesCellChecksResponse,
  EnergyRevenuesListResponse,
  TableTabs
} from "../RevenueMonitoring.types";
import { EnergyPriceUnitSwitch } from "./components/EnergyPriceUnitSwitch";
import { PRECISION_FOR_OUTDATED } from "./EnergyRevenues.constants";
import { EnergyVolumeOverviewChart } from "./EnergyRevenuesCharts/EnergyVolumeOverviewChart";
import { RevenueOverviewChart } from "./EnergyRevenuesCharts/RevenueOverviewChart";
import { EnergyRevenuesTable } from "./EnergyRevenuesTable/EnergyRevenuesTable";
import { EnergyRevenuesVerificationTable } from "./EnergyRevenuesVerificationTable/EnergyRevenuesVerificationTable";

export const TABLE_TAB_OPTIONS: Array<{
  label: ReactNode;
  value: TableTabs;
}> = [
  {
    label: (
      <Group gap="xs" wrap="nowrap">
        <IconTable size={16} />
        Aktuelle Erlöse
      </Group>
    ),
    value: "aktuelle-erloese"
  },
  {
    label: (
      <Group gap="xs" wrap="nowrap">
        <IconChecklist size={16} />
        Prüfansicht
      </Group>
    ),
    value: "pruefansicht"
  },
  {
    label: undefined,
    value: "pruefdetail"
  }
];

export interface EnergyRevenuesViewProps {
  energyRevenuesData: EnergyRevenuesListResponse;
  dataVerificationColumns?: Array<EnergyRevenuesCellCheckStateColumn>;
  dataVerificationItems?: EnergyRevenuesCellChecksResponse;
  companyId?: number;
  exportFileName: string;
  isLoading?: boolean;
}

interface TopToolbarProps {
  tableTab?: TableTabs;
  onTableTabChange: (value: TableTabs) => void;
  withUnitSwitch?: boolean;
}

function TopToolbar({
  tableTab,
  onTableTabChange,
  withUnitSwitch
}: TopToolbarProps) {
  return (
    <Flex align="center" gap="xl">
      <SegmentedControl
        data={TABLE_TAB_OPTIONS.filter((option) => option.label)}
        value={tableTab}
        onChange={onTableTabChange}
      />
      {withUnitSwitch && <EnergyPriceUnitSwitch />}
    </Flex>
  );
}

function EnergyRevenuesView({
  energyRevenuesData,
  dataVerificationColumns,
  dataVerificationItems,
  companyId,
  exportFileName,
  isLoading
}: EnergyRevenuesViewProps) {
  const { tableTab, setTableTab, verificationDetailId } =
    useEnergyRevenuesPageParams();

  const { energyPriceUnit } = useLsEnergyPriceUnit();

  const formattedForChartData = useMemo(
    () =>
      [...energyRevenuesData]
        .sort((revenuesA, revenuesB) =>
          sortBackendDates(revenuesA.period_start, revenuesB.period_start)
        )
        .map((data) => ({
          ...data,
          period_start: backendDateOrDateTimeToLuxonDateTime(
            data.period_start
          ).toFormat("MMM yyyy")
        })),
    [energyRevenuesData]
  );

  const errors = [
    ...new Map(
      energyRevenuesData
        .flatMap((item) => item.errors || [])
        .map((item) => [item.non_field_errors.join(" "), item])
    ).values()
  ];

  const MAX_WARNINGS_ABOVE_FOLD = 1;
  const warnings = [
    ...new Set(energyRevenuesData.flatMap((item) => item.warnings || []))
  ];

  function displayError(error: UserFacingErrorWithButton) {
    const msg = error.non_field_errors.join(" ");
    return (
      <Flex align="center" gap="sm" key={msg} mb="2" mt="2">
        {msg}
        <ErrorButton {...error} />
      </Flex>
    );
  }

  function handleTableTabChange(value: TableTabs) {
    if (value === "aktuelle-erloese") {
      Userpilot.track("em_aktuelle_erlose_tab");
    }
    if (value === "pruefansicht") {
      Userpilot.track("em_prufansicht_tab");
    }
    setTableTab(value);
  }

  const hasOutdatedVerifications = dataVerificationItems?.some(
    (verificationItem) =>
      energyRevenuesData.find(
        (revenueRow) =>
          revenueRow.period_start === verificationItem.period_start &&
          roundWithPrecision(
            Number(revenueRow[verificationItem.column]),
            PRECISION_FOR_OUTDATED
          ) !==
            roundWithPrecision(
              verificationItem.calculated_amount,
              PRECISION_FOR_OUTDATED
            )
      )
  );

  if (hasOutdatedVerifications) {
    warnings.push(
      "Es wurden Änderungen in den zugrunde liegenden Daten festgestellt, die zu neuen Berechnungsergebnissen im Erlösmonitoring geführt haben. Bitte nehmen Sie sich einen Moment Zeit, um die aktualisierten Werte zu überprüfen."
    );
  }

  return (
    <>
      {errors.length > 0 && (
        <Box mb="md">
          <Alert
            color={AlertPresets.warning.color}
            icon={AlertPresets.warning.icon}
          >
            <TruncatedList items={errors.map(displayError)} />
          </Alert>
        </Box>
      )}
      {warnings.length > 0 && (
        <Box mb="md">
          <Alert
            color={AlertPresets.warning.color}
            icon={AlertPresets.warning.icon}
          >
            <TruncatedList
              items={warnings}
              maxVisible={MAX_WARNINGS_ABOVE_FOLD}
            />
          </Alert>
        </Box>
      )}
      {companyId ? (
        <Tabs
          value={tableTab}
          onChange={(value) => value && setTableTab(value as TableTabs)}
        >
          <Tabs.Panel value="aktuelle-erloese">
            <EnergyRevenuesTable
              companyId={companyId}
              dataVerificationColumns={dataVerificationColumns}
              dataVerificationItems={dataVerificationItems}
              energyPriceUnit={energyPriceUnit as EnergyPriceUnits}
              energyRevenuesData={energyRevenuesData}
              exportFileName={exportFileName}
              isLoading={isLoading}
              toolbarComponents={
                <TopToolbar
                  tableTab={tableTab}
                  withUnitSwitch
                  onTableTabChange={handleTableTabChange}
                />
              }
            />
          </Tabs.Panel>
          <Tabs.Panel value="pruefansicht">
            <EnergyRevenuesVerificationTable
              companyId={companyId}
              dataVerificationItems={dataVerificationItems}
              energyRevenuesData={energyRevenuesData}
              exportFileName={exportFileName}
              isLoading={isLoading}
              toolbarComponents={
                <TopToolbar
                  tableTab={tableTab}
                  onTableTabChange={handleTableTabChange}
                />
              }
            />
          </Tabs.Panel>
          <Tabs.Panel value="pruefdetail">
            <EnergyRevenuesVerificationTable
              companyId={companyId}
              dataVerificationItems={
                dataVerificationItems
                  ? dataVerificationItems.filter(
                      (item) => item.id === verificationDetailId
                    )
                  : undefined
              }
              energyRevenuesData={energyRevenuesData}
              isLoading={isLoading}
              toolbarComponents={
                <TopToolbar
                  tableTab={tableTab}
                  withUnitSwitch
                  onTableTabChange={handleTableTabChange}
                />
              }
            />
          </Tabs.Panel>
        </Tabs>
      ) : (
        <EnergyRevenuesTable
          energyPriceUnit={energyPriceUnit}
          energyRevenuesData={energyRevenuesData}
          exportFileName={exportFileName}
          isLoading={isLoading}
        />
      )}
      <Group gap="lg" mb="lg">
        <RevenueOverviewChart
          formattedData={formattedForChartData}
          isLoading={isLoading}
        />
        <EnergyVolumeOverviewChart
          formattedData={formattedForChartData}
          isLoading={isLoading}
        />
      </Group>
    </>
  );
}

export { EnergyRevenuesView };
